import React, { useState } from 'react';
import { Box, Divider, IconButton, Tab, Tabs, Toolbar, Typography } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import FindingsPanel from '../../findings/components/FindingsPanel';
import KnowledgePanel from '../../knowledge/components/KnowledgePanel';
import WorkflowRunPanel from '../../workflow/components/WorkflowRunPanel';
import TapTooltip from '../../../shared/components/TapTooltip';

export type InvestigationDataTab = 'findings' | 'workflows' | 'knowledge';

type FindingsProps = Omit<React.ComponentProps<typeof FindingsPanel>, 'collapsed' | 'onToggleCollapse'>;
type KnowledgeProps = Omit<React.ComponentProps<typeof KnowledgePanel>, 'collapsed' | 'onToggleCollapse'>;
type RunsProps = React.ComponentProps<typeof WorkflowRunPanel>;

interface InvestigationDataPanelProps {
  tab: InvestigationDataTab;
  onTabChange: (tab: InvestigationDataTab) => void;
  findings: FindingsProps;
  knowledge: KnowledgeProps;
  /** The workflow runs panel; absent when there is no Workflows tab to show. */
  runs: RunsProps | null;
  /** Reloads findings, knowledge and runs. */
  onRefresh: () => void;
}

const TAB_SX = { minHeight: 40, minWidth: 'auto', textTransform: 'none' } as const;

/** The investigation's findings, workflow runs and knowledge, the right-hand column of the screen. */
const InvestigationDataPanel: React.FC<InvestigationDataPanelProps> = ({
  tab,
  onTabChange,
  findings,
  knowledge,
  runs,
  onRefresh,
}) => {
  const [findingsCollapsed, setFindingsCollapsed] = useState(false);
  const [knowledgeCollapsed, setKnowledgeCollapsed] = useState(true);
  // A Workflows tab that stopped rendering would leave `value` pointing at nothing, which MUI warns
  // about and which shows no selected tab at all.
  const activeTab = tab === 'workflows' && !runs ? 'findings' : tab;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 48,
          px: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          Investigation Data
        </Typography>
        <TapTooltip
          describeChild
          title="Refresh now. Findings, workflows and knowledge already refresh when the agent finishes a turn; this catches what someone else changed."
        >
          {/* The dashboard's turn-end effect covers this session's own agent. Nothing covers another
              operator advancing the same case, which is all this button is still for. */}
          <IconButton aria-label="Refresh investigation data" size="small" onClick={onRefresh} sx={{ ml: 1 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </TapTooltip>
      </Toolbar>

      {/* Tabs, not a vertical stack of collapsibles, but only once there is a third panel to
          reach. The workflow panel needs the column's full height to list runs, and nesting a
          second disclosure level inside a collapsed panel reads as an accordion inside an
          accordion. With workflows off and no run to read there are two panels and no reason to
          take away seeing both at once, so the stack below stays until the switch is removed. */}
      {runs ? (
        <>
          {/* Scrollable, so a narrow column scrolls the labels instead of squeezing them past their tabs. */}
          <Tabs
            value={activeTab}
            onChange={(_, value: InvestigationDataTab) => onTabChange(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Findings" value="findings" sx={TAB_SX} />
            <Tab
              label={runs.runs.length > 0 ? `Workflows (${runs.runs.length})` : 'Workflows'}
              value="workflows"
              sx={TAB_SX}
            />
            <Tab label="Knowledge" value="knowledge" sx={TAB_SX} />
          </Tabs>

          <Box sx={{ flex: 1, p: 1, minHeight: 0 }}>
            {activeTab === 'findings' && <FindingsPanel {...findings} />}
            {activeTab === 'workflows' && <WorkflowRunPanel {...runs} />}
            {activeTab === 'knowledge' && <KnowledgePanel {...knowledge} />}
          </Box>
        </>
      ) : (
        <>
          <Box sx={{ flex: findingsCollapsed ? 'none' : 1, p: 1, minHeight: 0 }}>
            <FindingsPanel
              {...findings}
              collapsed={findingsCollapsed}
              onToggleCollapse={() => setFindingsCollapsed(!findingsCollapsed)}
            />
          </Box>

          {!findingsCollapsed && !knowledgeCollapsed && <Divider sx={{ my: 1 }} />}

          <Box sx={{ flex: knowledgeCollapsed ? 'none' : 1, p: 1, minHeight: 0 }}>
            <KnowledgePanel
              {...knowledge}
              collapsed={knowledgeCollapsed}
              onToggleCollapse={() => setKnowledgeCollapsed(!knowledgeCollapsed)}
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default InvestigationDataPanel;
